import type React from 'react';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { SearchableDropdownField } from 'ui-components/components/dropdown';
import type { DropdownOption } from 'ui-components/components/dropdown/dropdown-types';
import { GuidService } from 'ui-components/services/guid-service';

import { Carriers } from '../../../../../../constants/carriers';
import { AU, NZ } from '../../../../../../constants/country-codes';
import { ANZSellerConfirmations, SellerConfirmations, SellerConfirmationsWithCanadaPost } from '../../../../../../enums/confirmation';
import Global from '../../../../../../store/global-selectors';
import type { WorkflowActionInputProps } from '../../../registries/registry-types';
import { messages } from '../messages';

const getOptionLabel = (option: DropdownOption) => option.label as string;
const getOptionValue = (option: DropdownOption) => String(option.ordinal);

export const ConfirmationDropdown: React.FC<WorkflowActionInputProps> = ({ className, draft, onUpdate }) => {
  const intl = useIntl();
  const id = useMemo(() => GuidService.newGuid(), []);
  const labelProviders = useSelector(Global.selectSellerLabelProviders);
  const homeCountry = useSelector(Global.selectSellerHomeCountry);

  const options = useMemo(() => {
    const hasCanadaPost = labelProviders.some((lp: { labelProviderId: string }) => lp.labelProviderId === Carriers.CANADA_POST.carrierId);

    let confirmations: Record<string, { messageDescriptor: object }>;
    if (hasCanadaPost) {
      confirmations = SellerConfirmationsWithCanadaPost;
    } else if (homeCountry === AU || homeCountry === NZ) {
      confirmations = ANZSellerConfirmations;
    } else {
      confirmations = SellerConfirmations;
    }

    return Object.values(confirmations).map(({ messageDescriptor }, ordinal) => ({
      ordinal,
      label: intl.formatMessage(messageDescriptor as Parameters<typeof intl.formatMessage>[0]),
    }));
  }, [labelProviders, homeCountry, intl]);

  const handleSelect = useCallback(
    (value: string) => {
      onUpdate({ intParameter: value ? Number(value) : null });
    },
    [onUpdate],
  );

  return (
    <SearchableDropdownField
      className={className}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      id={id}
      onItemSelected={handleSelect}
      options={options}
      placeholder={intl.formatMessage(messages.confirmationDropdownPlaceholder)}
      selectedItemValue={draft.intParameter !== null ? String(draft.intParameter) : undefined}
    />
  );
};
